import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  confirmPaymentMethods,
  createEnvelopeSendPayment,
  extractPaymentMethods,
  listPaymentMethods,
  syncPaymentMethods,
} from '../../services/billingService';
import { formatPayPerEnvelopePrice } from '../../utils/subscription';
import {
  getBillingErrorMessage,
  normalizePaymentFlowData,
} from '../../utils/billingPaymentFlow';
import { useStripeConfig } from './VolksignStripeProvider';
import PaymentMethodSelector from './PaymentMethodSelector';
import BillingPaymentModal from './BillingPaymentModal';

const PayPerEnvelopeModal = ({
  show,
  onHide,
  envelopeId,
  message,
  payPerEnvelope,
  onPaymentComplete,
  onViewPlans,
}) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isOrgAdmin = Boolean(
    user?.is_organization_admin ?? user?.is_organization_owner
  );
  const { ready: stripeReady, error: stripeConfigError } = useStripeConfig();

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  const [paymentSelection, setPaymentSelection] = useState('saved');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [paymentModal, setPaymentModal] = useState({
    show: false,
    clientSecret: null,
    amountCents: null,
    currency: 'usd',
    paymentIntentId: null,
  });

  const amountCents = payPerEnvelope?.amountCents ?? 99;
  const currency = payPerEnvelope?.currency ?? 'usd';
  const priceLabel = formatPayPerEnvelopePrice(amountCents, currency);

  const loadPaymentMethods = useCallback(async () => {
    setLoadingPaymentMethods(true);
    try {
      let data = await listPaymentMethods();
      let { methods, defaultId } = extractPaymentMethods(data);
      if (defaultId && methods.length === 0) {
        data = await syncPaymentMethods();
        ({ methods, defaultId } = extractPaymentMethods(data));
      }
      setPaymentMethods(methods);
      setDefaultPaymentMethodId(defaultId);
      if (methods.length > 0) {
        setPaymentSelection('saved');
        const initial =
          methods.find((pm) => pm.id === defaultId || pm.is_default) ?? methods[0];
        setSelectedPaymentMethodId(initial?.id ?? null);
      } else {
        setPaymentSelection('new');
      }
    } catch {
      setPaymentMethods([]);
      setPaymentSelection('new');
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, []);

  useEffect(() => {
    if (!show) {
      setError('');
      setPaymentModal({ show: false, clientSecret: null, amountCents: null, currency: 'usd', paymentIntentId: null });
      return undefined;
    }
    loadPaymentMethods();
    return undefined;
  }, [show, loadPaymentMethods]);

  const startPayment = useCallback(async () => {
    if (!envelopeId) {
      setError('Envelope must be saved before payment.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const useSaved = paymentSelection === 'saved' && paymentMethods.length > 0;

      const response = await createEnvelopeSendPayment(envelopeId, {
        paymentMethodSelection: useSaved ? 'saved' : 'new',
        paymentMethodId: useSaved ? selectedPaymentMethodId : undefined,
        useDefaultPaymentMethod:
          useSaved && !selectedPaymentMethodId && Boolean(defaultPaymentMethodId),
        savePaymentMethod: useSaved ? undefined : savePaymentMethod,
        setAsDefault: useSaved ? undefined : savePaymentMethod,
      });

      if (!response.success) {
        throw new Error(response.message || 'Could not start payment for this envelope.');
      }

      const flow = normalizePaymentFlowData(response.data);

      if (flow.paymentCompleted || flow.alreadyPaid) {
        onPaymentComplete?.();
        return;
      }

      if (flow.requiresPaymentUi && flow.clientSecret) {
        setPaymentModal({
          show: true,
          clientSecret: flow.clientSecret,
          amountCents: flow.amountCents ?? amountCents,
          currency: flow.currency ?? currency,
          paymentIntentId: flow.paymentIntentId,
        });
        return;
      }

      if (flow.requiresPaymentUi && !flow.clientSecret) {
        throw new Error('Payment could not be started. Try a saved card or retry.');
      }

      throw new Error('Unexpected payment response.');
    } catch (err) {
      setError(getBillingErrorMessage(err, 'Could not start payment for this envelope.'));
    } finally {
      setSubmitting(false);
    }
  }, [
    envelopeId,
    paymentSelection,
    paymentMethods.length,
    selectedPaymentMethodId,
    defaultPaymentMethodId,
    savePaymentMethod,
    amountCents,
    currency,
    onPaymentComplete,
  ]);

  const handlePaymentModalSuccess = async ({ paymentIntentId } = {}) => {
    const piId = paymentIntentId || paymentModal.paymentIntentId;
    if (piId && savePaymentMethod) {
      try {
        await confirmPaymentMethods({
          paymentIntentId: piId,
          setAsDefault: savePaymentMethod,
        });
      } catch {
        // Send can still proceed.
      }
    }
    setPaymentModal((prev) => ({ ...prev, show: false }));
    onPaymentComplete?.();
  };

  const handleUpgrade = () => {
    onHide();
    if (typeof onViewPlans === 'function') {
      onViewPlans();
      return;
    }
    navigate('/plans');
  };

  const payDisabled =
    submitting
    || !stripeReady
    || (paymentSelection === 'saved' && !selectedPaymentMethodId && !defaultPaymentMethodId);

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Send this envelope</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            {message
              || 'You have used all envelopes included in your plan for this billing period.'}
          </p>
          <p className="mb-3">
            Pay <strong>{priceLabel}</strong> to send this envelope, or upgrade your plan for more
            monthly envelopes.
          </p>

          {stripeConfigError && (
            <Alert variant="warning" className="small">{stripeConfigError}</Alert>
          )}

          {error && (
            <Alert variant="danger" className="small">
              {error}
            </Alert>
          )}

          {loadingPaymentMethods ? (
            <div className="text-center py-3">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <PaymentMethodSelector
              methods={paymentMethods}
              defaultId={defaultPaymentMethodId}
              selection={paymentSelection}
              onSelectionChange={setPaymentSelection}
              selectedPaymentMethodId={selectedPaymentMethodId}
              onPaymentMethodIdChange={setSelectedPaymentMethodId}
              savePaymentMethod={savePaymentMethod}
              onSavePaymentMethodChange={(checked) => {
                setSavePaymentMethod(checked);
              }}
              disabled={submitting}
              idPrefix="envelope-pay"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          {isOrgAdmin && (
            <Button variant="primary" onClick={handleUpgrade} disabled={submitting}>
              View plans
            </Button>
          )}
          <Button variant="primary" onClick={startPayment} disabled={payDisabled}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing…
              </>
            ) : (
              `Pay ${priceLabel} and send`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <BillingPaymentModal
        show={paymentModal.show}
        onHide={() => setPaymentModal((prev) => ({ ...prev, show: false }))}
        title={`Pay ${priceLabel} and send`}
        amountCents={paymentModal.amountCents}
        currency={paymentModal.currency}
        clientSecret={paymentModal.clientSecret}
        returnUrl={window.location.href}
        submitLabel={`Pay ${priceLabel} and send`}
        onSuccess={handlePaymentModalSuccess}
        onError={(err) => setError(getBillingErrorMessage(err))}
      />
    </>
  );
};

export default PayPerEnvelopeModal;
